import React, { useState, useEffect, useContext, lazy, Suspense } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";

import { UserContext } from "../context";
import { useSnackbar } from "../context/SnackbarProvider";
import { useMessageContext } from "../context/MessageProvider";
import { useNotificationbar } from "../context/NotificationBar";
import { useTokenVerification } from "../hooks/useTokenVerification";

import UserProtectedRoute from "./UserProtectedRoute";
import GlobalLoader from "../components/GlobalLoader";
//import Topbar from "../global/Topbar";

import InterestsDialog from "../components/dialogs/InterestsDialog";
import UnSubscribeNewsletter from "../pages/static_page/UnSubscribeNewsletter";
import MailUnsubscribe from "../pages/static_page/MailUnsubscribe";
import Messages from "../pages/messages/Messages";
import Navbar from "../global/Navbar";


const Landing = lazy(() => import("../pages/landing2/Landing"));
const Home = lazy(() => import("../pages/Home"));
//const Chats = lazy(() => import("../pages/chats/Chats"));
const Festivals = lazy(() => import("../pages/festivals/Festivals"));
const AllWorks = lazy(() => import("../pages/works/AllWorks"));
const WorkDetails = lazy(() => import("../pages/works/WorkDetails"));
const AllProject = lazy(() => import("../pages/projects/AllProject"));
const SingleProject = lazy(() => import("../pages/projects/SingleProject"));
const CreateProjectForm = lazy(() => import("../pages/projects/CreateProjectForm"));
const EditProjectForm = lazy(() => import("../pages/projects/EditProjectForm"));
const AllCompetition = lazy(() => import("../pages/competition/AllCompetition"));
const SingleCompetition = lazy(() => import("../pages/competition/SingleCompetition"));
const SignUp = lazy(() => import("../pages/auth/SignUp"));
const SignIn = lazy(() => import("../pages/auth/SignIn"));
const ForgotPassword = lazy(() => import("../pages/auth/ForgotPassword"));
//const People = lazy(() => import("../pages/People"));
const Profile = lazy(() => import("../pages/profile/Profile"));
const EditProfile = lazy(() => import("../pages/EditProfile"));
const Error = lazy(() => import("../pages/Error"));
const Searchpage = lazy(() => import("../pages/search/Searchpage"));
const AllEvent = lazy(() => import("../pages/events/AllEvent"));
const SingleEvent = lazy(() => import("../pages/events/SingleEvent"));
const ResetPassword = lazy(() => import("../pages/auth/ResetPassword"));
const Faq = lazy(() => import("../pages/static_page/Faq"));
const ContactUs = lazy(() => import("../pages/static_page/ContactUs"));
const About = lazy(() => import("../pages/static_page/About"));
const TermsCondition = lazy(() => import("../pages/static_page/TermsCondition"));
const PrivacyPolicy = lazy(() => import("../pages/static_page/PrivacyPolicy"));
const Disclaimer = lazy(() => import("../pages/static_page/Disclaimer"));
const People = lazy(() => import("../pages/people/People"));
const All_Notification = lazy(() => import("../pages/notification_page/All_Notification"));
const Authentication = lazy(() => import("../components/Authentication"));

const UserRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();
  const { user, refreshUser, isFetching } = useContext(UserContext);
  
  // Check if there's an ongoing onboarding process
  const onboardingInProgress = localStorage.getItem('onboarding_step');
  const [openInterestsDialog, setOpenInterestsDialog] = useState(!!onboardingInProgress);
  
  const { initializeRabbitMQ } = useMessageContext();
  const { showNotificationbar, setOpen } = useNotificationbar();

  // Initialize token verification
  useTokenVerification();

  const hasAnotherAccount =
    user?.have_another_account &&
    Object.keys(user.have_another_account).length > 0;

  const openDialogOfInterests = () => {
    setOpenInterestsDialog(true);
  };

  useEffect(() => {
    // Open dialog if user status is 2 OR if there's an ongoing onboarding process
    const onboardingInProgress = localStorage.getItem('onboarding_step');
    if (user?.status === 2 || onboardingInProgress) {
      openDialogOfInterests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (user?.username) {
      initializeRabbitMQ(user.username);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    const lastShownTime = localStorage.getItem("fc_last_notification_time");
    const currentTime = new Date().getTime();
    const oneDay = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
    //const oneDay = 1000 * 60; // 1 minute for testing

    if (
      user?.status !== 2 &&
      user?.account_type !== "unknown" &&
      user?.nudge === 0 &&
      user?.profile_completion_percentage < 100 &&
      location.pathname !== "/edit-profile" &&
      (!lastShownTime || currentTime - lastShownTime > oneDay)
    ) {
      showNotificationbar("profile_completion", {
        progress: user.profile_completion_percentage,
        onComplete: () => {
          setTimeout(() => {
            setOpen(false); // Close Snackbar
            navigate("/edit-profile"); // Navigate to edit-profile page
          }, 200); // Small delay to allow UI to update smoothly
        },
      });
      // Store the current time as the last shown time
      localStorage.setItem("fc_last_notification_time", currentTime);
    }
  }, [user]);

  if (isFetching) return;


  return (
    <React.Fragment>
      <Suspense fallback={<GlobalLoader />}>
        <Routes>
          {/* Public Routes */}
          {/* <Route element={<Topbar />}> */}
          <Route element={<Navbar />}>
            <Route
              path="/signup"
              element={user ? <Navigate to="/home" replace /> : <SignUp />}
            />
            <Route
              path="/signin"
              element={user ? <Navigate to="/home" replace /> : <SignIn />}
            />
            <Route
              path="/forgot-password"
              element={
                user ? <Navigate to="/home" replace /> : <ForgotPassword />
              }
            />
            <Route
              path="/reset-password/:token"
              element={user ? <Navigate to="/home" replace /> : <ResetPassword />}
            />

            {/* <Route path="/people"
              element={!user ? <People /> : <Navigate to="/my-network" replace />}
            /> */}

            <Route path="/people" element={<People />}/>

            <Route path="/home" element={<Home user={user} isFetching={isFetching} showSnackbar={showSnackbar} />} />
            <Route path="/home/post/:postId/:uniqueId" element={<Home />} />
            <Route path="/profile/:username" element={<Profile />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/about" element={<About />} />
            <Route path="/terms-and-conditions" element={<TermsCondition />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/social-auth" element={<Authentication />} />

            {/*Events Routes Start*/}
            <Route path="/events" element={<AllEvent />} />
            <Route path="/event/:eventId/:eventTitle" element={<SingleEvent />} />

            <Route path="/jobs" element={<AllWorks />} />
            <Route path="/job/:workId/:workTitle" element={<WorkDetails />} />
            <Route path="/projects" element={<AllProject />} />
            <Route path="/projects/:projectId/:projectTitle" element={<SingleProject />} />
            <Route path="/competitions" element={<AllCompetition />} />
            <Route path="/competition/:competitionId/:competitionTitle" element={<SingleCompetition user={user} />} />
            <Route path="/festivals" element={<Festivals />} />


            {/* Protected Routes */}
            <Route
              path="/sign-up"
              element={
                hasAnotherAccount ? (
                  <Navigate to="/home" replace />
                ) : (
                  <UserProtectedRoute isAuthenticated={!!user}>
                    <SignUp />
                  </UserProtectedRoute>
                )
              }
            />

            <Route
              path="/create-project"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <CreateProjectForm />
                </UserProtectedRoute>
              }
            />
            <Route
              path="/project/:projectId/edit"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <EditProjectForm />
                </UserProtectedRoute>
              }
            />
            <Route
              path="/project/:projectId/edit/:competitionId"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <EditProjectForm />
                </UserProtectedRoute>
              }
            />
            <Route
              path="/"
              element={user ? <Navigate to="/home" replace /> : <Landing />}
            />

            <Route
              path="/home/post/:postId/:uniqueId"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <Home />
                </UserProtectedRoute>
              }
            />

            <Route
              path="/home/post/:postId/:uniqueId/comment/:commentId"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <Home />
                </UserProtectedRoute>
              }
            />
            <Route
              path="/home/post/:postId/:uniqueId/comment/:commentId?/reply/:replyId?"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <Home />
                </UserProtectedRoute>
              }
            />
            <Route
              path="/home/hashtag/:hashtag"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <Home />
                </UserProtectedRoute>
              }
            />

            <Route
              path="/search"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <Searchpage />
                </UserProtectedRoute>
              }
            />


            <Route
              path="/edit-profile"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <EditProfile />
                </UserProtectedRoute>
              }
            />
            {/* <Route
              path="/chats"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <Chats />
                </UserProtectedRoute>
              }
            />
            <Route
              path="/chats/:Id"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <Chats />
                </UserProtectedRoute>
              }
            /> */}

            <Route
              path="/notification"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <All_Notification />
                </UserProtectedRoute>
              }
            />

            {/* added for message */}
            <Route
              path="/chats"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <Messages
                    user={user}
                    open={true}
                    onClose={() => navigate(-1)}
                    showSnackbar={showSnackbar}
                  />
                </UserProtectedRoute>
              }
            />

            <Route
              path="/chats/:Id"
              element={
                <UserProtectedRoute isAuthenticated={!!user}>
                  <Messages
                    user={user}
                    open={true}
                    onClose={() => navigate(-1)}
                    showSnackbar={showSnackbar}
                  />
                </UserProtectedRoute>
              }
            />

            <Route path="/newsletter-unsubscribed" element={<UnSubscribeNewsletter />} />
            <Route path="/mail-unsubscribed" element={<MailUnsubscribe />} />



            {/* Catch-all route */}
            <Route path="*" element={<Error />} />
          </Route>
        </Routes>
      </Suspense>

      {/* Pass openDialog and setOpenDialog as props to InterestsDialog */}
      <InterestsDialog
        open={openInterestsDialog}
        setOpen={setOpenInterestsDialog}
        onAllDone={() => refreshUser()}
        showSnackbar={showSnackbar}
      />
    </React.Fragment>
  );
};

export default UserRoutes;
